// src/component/QRCLanding.tsx
import React, { useState } from 'react';
import { 
  QrCode, 
  Link2, 
  BarChart3, 
  Shield, 
  Zap, 
  Globe, 
  ChevronRight, 
  Check, 
  Download, 
  Copy, 
  ExternalLink 
} from 'lucide-react';
import { QRCApiService, ShortenResponse } from '../../services/api';

const QRCLanding: React.FC = () => {
  const [url, setUrl] = useState<string>('');
  const [shortUrl, setShortUrl] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const generateQRAndShortLink = async (): Promise<void> => {
    if (!url) return;
    
    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    setIsGenerating(true);
    setError('');
    
    try {
      // Call your C# backend API using axios
      const response: ShortenResponse = await QRCApiService.shortenUrl({ 
        longUrl: url 
      });
      
      setShortUrl(response.shortUrl);
      setQrCode(response.qrCodeUrl);
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to generate short link. Please try again.';
      setError(errorMessage);
      console.error('Error generating short link:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const downloadQR = (): void => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `qr-code-${shortUrl.split('/').pop()}.png`;
    link.click();
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter' && url && !isGenerating) {
      generateQRAndShortLink();
    }
  };

  const resetForm = (): void => {
    setUrl('');
    setShortUrl('');
    setQrCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-lg">
                <QrCode className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">qr-c.io</span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-300 hover:text-white transition">
                Features
              </a>
              <a href="#pricing" className="text-gray-300 hover:text-white transition">
                Pricing
              </a>
              <a href="#docs" className="text-gray-300 hover:text-white transition">
                Docs
              </a>
              <button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition">
                Get Started
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2 mb-6">
              <Zap className="w-4 h-4 text-purple-400 mr-2" />
              <span className="text-purple-300 text-sm font-medium">
                Lightning Fast Link Shortening
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Shorten Links.<br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Generate QR Codes.
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-12">
              Create branded short links and dynamic QR codes in seconds. Track performance, engage your audience, and grow your business.
            </p>

            {/* URL Input Section */}

          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-black/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Everything you need to manage, track, and optimize your links
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <BarChart3 className="w-6 h-6 text-white" />,
                gradient: 'from-purple-500 to-pink-500',
                title: 'Advanced Analytics',
                description: 'Track clicks, geographic data, devices, and referrers in real-time with detailed insights.'
              },
              {
                icon: <QrCode className="w-6 h-6 text-white" />,
                gradient: 'from-blue-500 to-cyan-500',
                title: 'Dynamic QR Codes',
                description: 'Generate customizable QR codes that can be updated without reprinting.'
              },
              {
                icon: <Shield className="w-6 h-6 text-white" />,
                gradient: 'from-green-500 to-emerald-500',
                title: 'Enterprise Security',
                description: 'Bank-grade encryption, password protection, and link expiration for maximum security.'
              },
              {
                icon: <Globe className="w-6 h-6 text-white" />,
                gradient: 'from-orange-500 to-red-500',
                title: 'Custom Domains',
                description: 'Use your own branded domain for professional short links that build trust.'
              },
              {
                icon: <Zap className="w-6 h-6 text-white" />,
                gradient: 'from-yellow-500 to-orange-500',
                title: 'API Access',
                description: 'Integrate with your tools using our robust RESTful API and webhooks.'
              },
              {
                icon: <Link2 className="w-6 h-6 text-white" />,
                gradient: 'from-pink-500 to-purple-500',
                title: 'Link Management',
                description: 'Organize links with tags, folders, and bulk operations for easy management.'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition"
              >
                <div className={`bg-gradient-to-br ${feature.gradient} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {[
              { value: '10M+', label: 'Links Created' },
              { value: '500K+', label: 'Active Users' },
              { value: '99.9%', label: 'Uptime' },
              { value: '150+', label: 'Countries' }
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-black/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to get started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of businesses using qr-c.io to shorten links and track performance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-lg font-medium hover:shadow-lg hover:shadow-purple-500/50 transition flex items-center justify-center">
              Start Free Trial
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
            <button className="bg-white/10 border border-white/20 text-white px-8 py-4 rounded-lg font-medium hover:bg-white/20 transition">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-2 rounded-lg">
                  <QrCode className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white">qr-c.io</span>
              </div>
              <p className="text-gray-400 text-sm">
                The fastest way to create short links and QR codes for your business.
              </p>
            </div>
            
            {[
              {
                title: 'Product',
                links: ['Features', 'Pricing', 'API', 'Integrations']
              },
              {
                title: 'Company',
                links: ['About', 'Blog', 'Careers', 'Contact']
              },
              {
                title: 'Legal',
                links: ['Privacy', 'Terms', 'Security', 'Compliance']
              }
            ].map((column, index) => (
              <div key={index}>
                <h4 className="text-white font-semibold mb-4">{column.title}</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  {column.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a href="#" className="hover:text-white transition">
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 qr-c.io. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes fadeIn {
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QRCLanding;